import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MessageCircle, Clock, MapPin, Send } from 'lucide-react';

const ContactPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour à l'accueil
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                    {/* Title */}
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                            <MessageCircle className="w-7 h-7 text-blue-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">Contactez-nous</h1>
                            <p className="text-slate-500">Notre équipe est à votre disposition</p>
                        </div>
                    </div>

                    {/* Contact Cards */}
                    <div className="grid md:grid-cols-2 gap-6 mb-10">
                        {/* Email Card */}
                        <a 
                            href="mailto:welcome@phoneb2b.com"
                            className="group bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-6 hover:border-blue-400 hover:shadow-lg transition-all"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <Mail className="w-7 h-7 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm text-blue-600 font-medium">Email</p>
                                    <p className="text-xl font-bold text-slate-900">welcome@phoneb2b.com</p>
                                </div>
                            </div>
                            <p className="text-slate-600 text-sm mb-4">
                                Pour toutes vos questions, demandes de devis ou informations sur nos produits.
                            </p>
                            <div className="flex items-center gap-2 text-blue-600 font-medium">
                                <Send className="w-4 h-4" />
                                Envoyer un email
                            </div>
                        </a>

                        {/* WhatsApp Card */}
                        <a 
                            href="https://wa.me/33648500231"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-6 hover:border-emerald-400 hover:shadow-lg transition-all"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <Phone className="w-7 h-7 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm text-emerald-600 font-medium">WhatsApp</p>
                                    <p className="text-xl font-bold text-slate-900">+33 6 48 50 02 31</p>
                                </div>
                            </div>
                            <p className="text-slate-600 text-sm mb-4">
                                Réponse rapide pour vos questions urgentes ou pour discuter de vos besoins.
                            </p>
                            <div className="flex items-center gap-2 text-emerald-600 font-medium">
                                <MessageCircle className="w-4 h-4" />
                                Ouvrir WhatsApp
                            </div>
                        </a>
                    </div>

                    {/* Info Section */}
                    <div className="bg-slate-50 rounded-xl p-6 mb-8">
                        <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <Clock className="w-5 h-5 text-blue-600" />
                            Horaires de disponibilité
                        </h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                                <p className="text-sm text-slate-500 mb-1">Lundi - Vendredi</p>
                                <p className="font-semibold text-slate-900">9h00 - 18h00</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                                <p className="text-sm text-slate-500 mb-1">Samedi - Dimanche</p>
                                <p className="font-semibold text-slate-900">Fermé</p>
                            </div>
                        </div>
                        <p className="text-sm text-slate-500 mt-4">
                            Les messages reçus en dehors des heures d'ouverture seront traités dès le prochain jour ouvré.
                        </p>
                    </div>

                    {/* Address Section */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
                        <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-blue-600" />
                            Notre adresse
                        </h2>
                        <div className="text-slate-700">
                            <p className="font-semibold">ECO MOBILE SOLUTION</p>
                            <p>ZI N2 CD 70 Le Biplan</p>
                            <p>Route d'Hérin</p>
                            <p>59220 Rouvignies, France</p>
                        </div>
                    </div>

                    {/* FAQ Teaser */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                        <h2 className="font-bold text-amber-900 mb-2">Vous avez des questions ?</h2>
                        <p className="text-amber-800 text-sm mb-4">
                            Consultez notre guide des grades cosmétiques ou nos conditions générales de vente pour plus d'informations.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link 
                                to="/grades"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-sm font-medium border border-amber-300"
                            >
                                Guide des grades
                            </Link>
                            <Link 
                                to="/cgv"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-sm font-medium border border-amber-300"
                            >
                                CGV
                            </Link>
                        </div>
                    </div>

                    {/* Back to home */}
                    <div className="mt-8 text-center">
                        <Link 
                            to="/" 
                            onClick={() => window.scrollTo(0, 0)}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors font-medium"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Retour à l'accueil
                        </Link>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ContactPage;
